import React, { useCallback, useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { Modal } from 'antd'

/**
 * Hook that blocks navigation (router transitions, beforeunload, custom actions)
 * whenever the view has unsaved edits.
 *
 * Example:
 *
 * const guardUnsaved = useUnsavedChangesProtection(isDirty)
 *
 * const handleNavigateAway = () => {
 *   guardUnsaved(() => {
 *     history.push('/next-route')
 *   })
 * }
 *
 * @param {boolean} hasUnsavedChanges - indicates whether the view has changes to protect
 * @returns {(nextAction: Function) => void} guard callback wrapping navigation logic
 */

const confirmDiscardChanges = () => {
  // Prompt user to confirm that losing unsaved edits is acceptable
  return new Promise((resolve) => {
    Modal.confirm({
      title: 'Discard unsaved changes?',
      icon: <ExclamationCircleOutlined />,
      content: 'You have unsaved changes. Continuing will discard them.',
      okText: 'Discard changes',
      okType: 'danger',
      cancelText: 'Stay on page',
      centered: true,
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    })
  })
}

export const useUnsavedChangesProtection = (hasUnsavedChanges) => {
  const history = useHistory()
  const locationKeysRef = useRef([history.location?.key || 'initial'])
  const pendingTransitionRef = useRef(null)
  const isConfirmingRef = useRef(false)

  const guardAction = useCallback(async (nextAction) => {
    // Run action immediately when view is clean (no unsaved changes flagged)
    if (!hasUnsavedChanges) {
      nextAction()
      return
    }

    const shouldDiscard = await confirmDiscardChanges()
    if (shouldDiscard) {
      nextAction()
    }
  }, [hasUnsavedChanges])

  useEffect(() => {
    // Track history keys so POP transitions (browser back/forward) can be replayed later
    const unlisten = history.listen((location, action) => {
      if (action === 'PUSH') {
        locationKeysRef.current = [...locationKeysRef.current, location.key]
        return
      }

      if (action === 'REPLACE') {
        locationKeysRef.current = [
          ...locationKeysRef.current.slice(0, -1),
          location.key,
        ]
        return
      }

      if (action === 'POP') {
        const nextIndex = locationKeysRef.current.lastIndexOf(location.key)
        if (nextIndex >= 0) {
          locationKeysRef.current = locationKeysRef.current.slice(0, nextIndex + 1)
        } else {
          locationKeysRef.current = [location.key]
        }
      }
    })

    return unlisten
  }, [history])

  // When dirty, register window/unblock handlers that gate all navigation
  useEffect(() => {
    if (!hasUnsavedChanges || typeof window === 'undefined') {
      return undefined
    }

    const handleBeforeUnload = (event) => {
      event.preventDefault()
      event.returnValue = ''
    }

    const cleanupBeforeUnload = () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    const unblock = history.block((nextLocation, action) => {
      // Prevent router transition until user confirms
      if (isConfirmingRef.current) {
        return false
      }

      const currentKey = history.location?.key
      const keys = locationKeysRef.current
      const currentIndex = currentKey ? keys.lastIndexOf(currentKey) : -1
      const targetIndex = nextLocation?.key ? keys.lastIndexOf(nextLocation.key) : -1
      const delta = action === 'POP' && currentIndex !== -1 && targetIndex !== -1
        ? targetIndex - currentIndex
        : null

      pendingTransitionRef.current = {
        action,
        delta,
        location: nextLocation,
      }

      isConfirmingRef.current = true

      confirmDiscardChanges().then((shouldDiscard) => {
        isConfirmingRef.current = false

        if (!shouldDiscard) {
          pendingTransitionRef.current = null
          return
        }

        const confirmedTransition = pendingTransitionRef.current
        pendingTransitionRef.current = null

        unblock()
        cleanupBeforeUnload()

        if (!confirmedTransition) {
          return
        }

        if (confirmedTransition.action === 'POP') {
          if (typeof confirmedTransition.delta === 'number' && confirmedTransition.delta !== 0) {
            history.go(confirmedTransition.delta)
          } else if (confirmedTransition.location) {
            navigateToLocation(history, confirmedTransition.location)
          }
        } else if (confirmedTransition.action === 'REPLACE') {
          navigateToLocation(history, confirmedTransition.location, 'replace')
        } else {
          navigateToLocation(history, confirmedTransition.location)
        }
      })

      return false
    })

    return () => {
      cleanupBeforeUnload()
      unblock()
      pendingTransitionRef.current = null
      isConfirmingRef.current = false
    }
  }, [hasUnsavedChanges, history])

  return guardAction
}

const navigateToLocation = (historyInstance, location, method = 'push') => {
  // Replay the intended navigation once user approved it
  if (!location || typeof historyInstance[method] !== 'function') {
    return
  }

  historyInstance[method]({
    hash: location.hash,
    pathname: location.pathname,
    search: location.search,
    state: location.state,
  })
}
